import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { useT } from "@/context/ContentContext";
import {
  EVENT_TYPES, getEventTypeMeta, formatEventDate, formatEventRange, useGroupedEvents,
} from "@/lib/events";
import { CalendarDays, MapPin } from "lucide-react";

function EventCard({ ev }) {
  const meta = getEventTypeMeta(ev.event_type);
  const d = formatEventDate(ev.starts_at, ev.all_day);
  return (
    <article className="card-dark p-5 flex gap-5" data-testid={`event-${ev.id}`}>
      {/* date stamp */}
      <div className="shrink-0 w-16 sm:w-20 text-center rounded-lg border border-white/10 bg-white/5 py-2 self-start">
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#00E5FF]">{d.month}</div>
        <div className="font-display text-3xl sm:text-4xl text-chrome leading-none mt-1">{d.day}</div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 mt-1">{d.weekday}</div>
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="font-varsity text-xl tracking-wider">{ev.title}</div>
          <span className={`text-[10px] uppercase tracking-[0.18em] border px-2 py-0.5 rounded-full ${meta.accent}`}>
            {meta.label}
          </span>
        </div>
        <div className="mt-2 text-sm text-white/65 flex items-center gap-3 flex-wrap">
          <span className="inline-flex items-center gap-1.5"><CalendarDays size={13} /> {formatEventRange(ev.starts_at, ev.ends_at, ev.all_day)}</span>
          {ev.location && (
            <span className="inline-flex items-center gap-1.5 text-white/55"><MapPin size={13} /> {ev.location}</span>
          )}
        </div>
        {ev.description && (
          <p className="mt-3 text-sm text-white/65 leading-relaxed whitespace-pre-wrap">{ev.description}</p>
        )}
      </div>
    </article>
  );
}

export default function Events() {
  const t = useT();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get("/events")
      .then((r) => setEvents(r.data))
      .finally(() => setLoading(false));
  }, []);

  const visible = filter === "all" ? events : events.filter((e) => e.event_type === filter);
  const groups = useGroupedEvents(visible);

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      <main className="mx-auto max-w-5xl w-full px-6 py-16 flex-1">
        <div className="overline mb-3">{t("events.page.overline", "Schedule")}</div>
        <h1 className="font-display uppercase text-4xl sm:text-5xl text-chrome">{t("events.page.title", "Upcoming Events")}</h1>
        <p className="mt-4 text-white/65 max-w-2xl">{t("events.page.subtitle", "")}</p>

        <div className="mt-8 flex flex-wrap gap-2" data-testid="events-filters">
          <button
            onClick={() => setFilter("all")}
            className={`px-3 py-1.5 text-xs uppercase tracking-[0.15em] rounded-full border ${
              filter === "all" ? "bg-white text-black border-white" : "border-white/15 text-white/70 hover:border-[#00E5FF]/40"
            }`}
            data-testid="events-filter-all"
          >
            All
          </button>
          {EVENT_TYPES.map((tp) => (
            <button
              key={tp.value}
              onClick={() => setFilter(tp.value)}
              className={`px-3 py-1.5 text-xs uppercase tracking-[0.15em] rounded-full border ${
                filter === tp.value ? "bg-white text-black border-white" : "border-white/15 text-white/70 hover:border-[#00E5FF]/40"
              }`}
              data-testid={`events-filter-${tp.value}`}
            >
              {tp.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="mt-12 space-y-4">
            {[0, 1, 2].map((i) => <div key={i} className="card-dark h-32 animate-pulse" />)}
          </div>
        ) : groups.length === 0 ? (
          <div className="card-dark mt-12 p-12 text-center text-white/60" data-testid="events-empty">
            <CalendarDays className="mx-auto mb-3 text-[#00E5FF]" />
            No upcoming events. Check back soon!
          </div>
        ) : (
          <div className="mt-12 space-y-12" data-testid="events-list">
            {groups.map((g) => (
              <section key={g.label}>
                <div className="font-varsity tracking-[0.25em] text-white/60 text-sm uppercase mb-4">{g.label}</div>
                <div className="space-y-3">
                  {g.items.map((ev) => <EventCard key={ev.id} ev={ev} />)}
                </div>
              </section>
            ))}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
